// src/pages/ChatBot/evals/run-eval-modal.tsx
//
// Confirmation dialog for starting an evaluation run, split out of EvalsPanel.
// Gates the (costly, capacity-sharing) run behind an explicit confirm. Follows
// the house modal shell pattern (cf. configs/config-modals.tsx) with eval-modal-*
// styles from EvalsPanel.css, which the parent that mounts this imports. Escape
// and backdrop map to Cancel — both suspended while the POST is in flight so the
// dialog can't vanish underneath the request.

import { useEscapeClose } from '../../../hooks/useEscapeClose';
import { useModalBackdropClose } from '../../../hooks/useModalBackdropClose';

interface RunEvalConfirmModalProps {
  open: boolean;
  isStarting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function RunEvalConfirmModal({
  open,
  isStarting,
  onConfirm,
  onCancel,
}: RunEvalConfirmModalProps) {
  const backdropHandlers = useModalBackdropClose(() => {
    if (!isStarting) onCancel();
  });
  useEscapeClose(onCancel, open && !isStarting);
  if (!open) return null;

  return (
    <div className="eval-modal-overlay" role="presentation" {...backdropHandlers}>
      <div
        className="eval-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="eval-run-title"
      >
        <div className="eval-modal-header">
          <h2 id="eval-run-title" className="eval-modal-title">
            Run evaluation?
          </h2>
        </div>
        <div className="eval-modal-body">
          <p className="eval-modal-text">
            This replays every golden-set question through the full retrieval and answer
            pipeline, then scores the results with an AI judge. Each question incurs real
            API cost (retrieval, answer generation, and judging), and the run shares the
            chatbot's capacity — a large golden set can slow live queries while it runs.
            Results appear here when the run completes.
          </p>
        </div>
        <div className="eval-modal-footer">
          <button
            type="button"
            className="eval-modal-btn eval-modal-btn-ghost"
            onClick={onCancel}
            disabled={isStarting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="eval-modal-btn eval-modal-btn-primary"
            onClick={onConfirm}
            disabled={isStarting}
          >
            {isStarting ? 'Starting…' : 'Run evaluation'}
          </button>
        </div>
      </div>
    </div>
  );
}
